/**
 * Dashboard Filter Bar
 *
 * Provides: Start Date, End Date, Brand, Store, City (feature-flagged).
 * All Dashboard content reacts to these filters.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Chip,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { FilterAltOff } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { startOfYear } from 'date-fns';
import { fetchFilterOptions } from '../../services/filtersApi';
import { FEATURE_FLAGS } from '../../config/featureFlags';

export interface DashboardFilters {
  brands: string[];
  categories: string[];
  stores: string[];
  cities: string[];
  fromDate: Date;
  toDate: Date;
}

interface DashboardFilterBarProps {
  filters: DashboardFilters;
  onChange: (filters: DashboardFilters) => void;
}

const fieldSx = {
  '& .MuiInputBase-root': { fontSize: '12px', minHeight: 36 },
  '& .MuiInputLabel-root': { fontSize: '12px' },
  minWidth: 140,
  flex: '1 1 0',
};

const chipSx = {
  fontSize: '11px',
  height: 22,
  bgcolor: 'var(--color-accent-bg, #f5f0e8)',
  color: 'var(--color-accent, #8b7355)',
  '& .MuiChip-deleteIcon': { color: 'var(--color-accent, #8b7355)', fontSize: 14 },
};

const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({ filters, onChange }) => {
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [storeOptions, setStoreOptions] = useState<any[]>([]);
  const [cityOptions, setCityOptions] = useState<string[]>([]);

  useEffect(() => {
    fetchFilterOptions()
      .then((data) => {
        setBrandOptions(data.brands || []);
        setCategoryOptions(data.categories || []);
        setStoreOptions(data.stores || []);
        setCityOptions(data.cities || []);
      })
      .catch(() => {});
  }, []);

  /* Cascaded stores: filter by selected cities */
  const filteredStores = useMemo(() => {
    if (filters.cities.length === 0) return storeOptions;
    return storeOptions.filter((s: any) => filters.cities.includes(s.city));
  }, [storeOptions, filters.cities]);

  const update = (partial: Partial<DashboardFilters>) => {
    onChange({ ...filters, ...partial });
  };

  const activeCount = filters.brands.length + filters.categories.length + filters.stores.length + filters.cities.length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 1,
          mb: 3,
          p: '8px 12px',
          bgcolor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        }}
      >
        {/* Start Date */}
        <DatePicker
          label="Start Date"
          value={filters.fromDate}
          onChange={(d) => d && update({ fromDate: d })}
          slotProps={{
            textField: {
              size: 'small',
              sx: { ...fieldSx, minWidth: 120, flex: '0 0 auto' },
            },
          }}
        />

        {/* End Date */}
        <DatePicker
          label="End Date"
          value={filters.toDate}
          onChange={(d) => d && update({ toDate: d })}
          minDate={filters.fromDate}
          slotProps={{
            textField: {
              size: 'small',
              sx: { ...fieldSx, minWidth: 120, flex: '0 0 auto' },
            },
          }}
        />

        <Box sx={{ width: 0, height: 20, borderLeft: '1px solid var(--color-border)', flex: '0 0 auto' }} />

        {/* Brand */}
        <Autocomplete
          multiple
          size="small"
          options={brandOptions}
          value={filters.brands}
          onChange={(_, v) => update({ brands: v })}
          renderInput={(params) => <TextField {...params} label="Brand" placeholder="All" />}
          renderTags={(val, getTagProps) =>
            val.map((opt, i) => {
              const { key, ...props } = getTagProps({ index: i });
              return <Chip key={key} label={opt} size="small" {...props} sx={chipSx} />;
            })
          }
          limitTags={2}
          sx={fieldSx}
          disableCloseOnSelect
        />

        {/* Category */}
        <Autocomplete
          multiple
          size="small"
          options={categoryOptions}
          value={filters.categories}
          onChange={(_, v) => update({ categories: v })}
          renderInput={(params) => <TextField {...params} label="Category" placeholder="All" />}
          renderTags={(val, getTagProps) =>
            val.map((opt, i) => {
              const { key, ...props } = getTagProps({ index: i });
              return <Chip key={key} label={opt} size="small" {...props} sx={chipSx} />;
            })
          }
          limitTags={2}
          sx={fieldSx}
          disableCloseOnSelect
        />

        {/* City (feature-flagged) */}
        {FEATURE_FLAGS.ENABLE_CITY_FILTER && (
          <Autocomplete
            multiple
            size="small"
            options={cityOptions}
            value={filters.cities}
            onChange={(_, v) => update({ cities: v })}
            renderInput={(params) => <TextField {...params} label="City" placeholder="All" />}
            renderTags={(val, getTagProps) =>
              val.map((opt, i) => {
                const { key, ...props } = getTagProps({ index: i });
                return <Chip key={key} label={opt} size="small" {...props} sx={chipSx} />;
              })
            }
            limitTags={2}
            sx={fieldSx}
            disableCloseOnSelect
          />
        )}

        {/* Store */}
        <Autocomplete
          multiple
          size="small"
          options={filteredStores}
          value={filters.stores.map((id) => storeOptions.find((s: any) => s.store_id === id)).filter(Boolean)}
          onChange={(_, v) => update({ stores: v.map((s: any) => s.store_id) })}
          getOptionLabel={(opt: any) => opt.store_name || opt.store_id}
          renderInput={(params) => <TextField {...params} label="Store" placeholder="All" />}
          renderTags={(val, getTagProps) =>
            val.map((opt: any, i) => {
              const { key, ...props } = getTagProps({ index: i });
              return <Chip key={key} label={opt.store_name} size="small" {...props} sx={chipSx} />;
            })
          }
          limitTags={1}
          sx={fieldSx}
          disableCloseOnSelect
          isOptionEqualToValue={(opt: any, val: any) => opt.store_id === val.store_id}
        />

        {/* Clear all */}
        {activeCount > 0 && (
          <Tooltip title="Clear all filters">
            <IconButton
              size="small"
              onClick={() => update({ brands: [], categories: [], stores: [], cities: [] })}
              sx={{ color: 'var(--color-text-muted)' }}
            >
              <FilterAltOff sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}

        {activeCount > 0 && (
          <Typography sx={{ fontSize: '11px', color: 'var(--color-text-muted)', ml: 'auto' }}>
            {activeCount} filter{activeCount > 1 ? 's' : ''} active
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardFilterBar;
